import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";
import { API_BASE_URL } from "../../config";
import * as enquiryService from "../services/manageEnquiryService";

const token = () => localStorage.getItem("cms_token");
const headers = () => ({
  Authorization: `Bearer ${token()}`,
  "Content-Type": "application/json",
});

// Lead `status` codes as sent by get-trademark-enquiries (string values):
// "2" = negative, "4" = marked for future, "7" = converted / done
const DONE_STATUS = "7";

/**
 * Pull the API's own error message out of an axios failure.
 *
 * The backend returns a real reason in the response body even on non-2xx
 * (e.g. 404 + { status: false, message: "This user does not have a lead
 * assignment configuration." }). Axios only puts "Request failed with status
 * code 404" on err.message, so read the body first and fall back afterwards.
 */
export const apiErrorMessage = (err, fallback = "Something went wrong") => {
  const data = err?.response?.data;
  if (data) {
    if (typeof data === "string" && data.trim()) return data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    // Laravel-style { errors: { field: ["msg"] } }
    const firstFieldError = Object.values(data.errors || {})[0];
    if (Array.isArray(firstFieldError) && firstFieldError[0]) return firstFieldError[0];
  }
  return err?.message || fallback;
};

// ==================== QUERY HOOKS ====================

/**
 * Hook to fetch paginated enquiry list with filters
 */
export const useEnquiryList = (page, filterData) => {
  return useQuery({
    queryKey: ["enquiryList", page, filterData],
    queryFn: () => enquiryService.fetchEnquiryList(page, filterData),
    staleTime: 0,
    keepPreviousData: true,
    retry: 2,
    refetchOnMount: "always",
  });
};

/**
 * Hook to fetch single enquiry details
 */
export const useEnquiryDetails = (id) => {
  return useQuery({
    queryKey: ["enquiryDetails", id],
    queryFn: () => enquiryService.fetchEnquiryDetails(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
  });
};

/**
 Fetch internal user list
 */
export const useInternalUserList = () =>
  useQuery({
    queryKey: ["internalUserList"],
    queryFn: () => enquiryService.fetchInternalUserList(1, 100),
    select: (res) => res.data, // unwrap → plain array
    staleTime: 5 * 60 * 1000, // cache 5 min (list rarely changes)
  });

/**
 * Alias kept so existing code that still imports useAdminList keeps working.
 * Both hooks hit the same /get-internal-user-list endpoint.
 */
export const useAdminList = useInternalUserList;

// /**
//  * Hook to allocate selected leads to an internal user.
//  * Endpoint : POST /manual-asign-lead-to-user
//  * Payload  : { internal_user_id: number, lead_ids: number[] }
//  */
// export const useAllocateLeads = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: ({ adminId, leadIds }) =>
//       enquiryService.allocateLeads({ internal_user_id: adminId, lead_ids: leadIds }),
//     onSuccess: () => {
//       // Refresh the lead list so "Assigned To" column updates immediately
//       queryClient.invalidateQueries({ queryKey: ["enquiryList"] });
//       // Also bust the internal-user cache so lead counts re-fetch on next open
//       queryClient.invalidateQueries({ queryKey: ["internalUserList"] });
//     },
//     onError: (error) => {
//       toast.error(error?.response?.data?.message || error.message || "Failed to allocate leads");
//     },
//   });
// };

/**
 * Hook to fetch lead follow-up status options from API.
 * Endpoint: GET /lead-follow-up-options
 * Returns array of { id, category, label, option_value, is_active, ... }
 */
export const useFollowUpOptions = () => {
  return useQuery({
    queryKey: ["followUpOptions"],
    queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/lead-follow-up-options`, {
        headers: headers(),
      });
      return res.data?.data || [];
    },
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });
};

export const useAllocateLeads = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ adminId, leadIds }) => {
      let res;
      try {
        res = await axios.post(
          `${API_BASE_URL}/manual-asign-lead-to-user`,
          { internal_user_id: adminId, lead_ids: leadIds },
          { headers: headers() },
        );
      } catch (err) {
        // Non-2xx (404/422/500…): surface the API's own message, not axios's
        // generic "Request failed with status code NNN".
        throw new Error(apiErrorMessage(err, "Failed to allocate leads"));
      }

      if (
        res.data &&
        (res.data.status === false ||
          res.data.status === 0 ||
          res.data.status === 400 ||
          res.data.status === 500)
      ) {
        throw new Error(res.data.message || "Allocation failed");
      }

      return res.data;
    },

    // No toast here on purpose — every caller either shows its own Swal alert
    // (list pages) or its own toast (EnquiryFollowUp auto-assign). Toasting from
    // the hook too would double up on the Swal screens.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["internalUserList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
  });
};

// Fetch lead with follow-ups
export const useLeadDetailsFollowUp = (id) => {
  return useQuery({
    queryKey: ["lead-details", id],
    queryFn: () => enquiryService.fetchLeadDetails(id),
    enabled: !!id,
    select: (res) => res.data,
  });
};

// ── NEW: Inbox leads (assigned to current admin) ─────────────────────────────
export const useInboxLeads = (page, filters = {}) => {
  return useQuery({
    queryKey: ["inbox-leads", page, filters],
   queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
        headers: headers(),
        params: {
          page,
          limit: 10,
          is_assigned: 1,
          assign_to:
            filters.assign_to || localStorage.getItem("trade_user_id") || "",
          search: filters.search || "",
          start_date: filters.start_date || "",
          end_date: filters.end_date || "",
          is_positive: filters.is_positive || "",
        },
      });

     const allData = res.data?.data || [];
      const today = moment().format("YYYY-MM-DD");

      const inboxData = allData.filter((item) => {
        // Converted leads live in the Done list — they are finished work and must
        // not sit in the assigned/inbox working list. The API sends status as a
        // string ("7"), so compare as string.
        if (String(item.status) === DONE_STATUS) return false;

        const nfd = item.next_followup_date;

        // No follow-up date → show in inbox (already filtered by is_assigned=1 in params)
        if (!nfd) return true;

        // Parse DD-MM-YYYY to YYYY-MM-DD for reliable comparison
        const parsed = moment(nfd, "DD-MM-YYYY");
        if (!parsed.isValid()) return true;

        const nfdFormatted = parsed.format("YYYY-MM-DD");

        // Show in inbox if follow-up date is today or in the past
        return nfdFormatted <= today;
      });

      return {
        data: inboxData,
        total: inboxData.length,
        totalPages: Math.ceil(inboxData.length / 10) || 1,
      };
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

// Fetch future leads
export const useFutureLeads = (page, filters = {}) => {
  return useQuery({
    queryKey: ["future-leads", page, filters],
    queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
        headers: headers(),
        params: {
          page,
          limit: 10,
          status: 4,
          search: filters.search || "",
          start_date: filters.start_date || "",
          end_date: filters.end_date || "",
        },
      });
       const allData = res.data?.data || [];
      const today = moment().format("YYYY-MM-DD");

      const futureData = allData.filter((item) => {
        const nfd = item.next_followup_date;
        const isMarkedFuture = String(item.status) === "4";

        if (!nfd) return isMarkedFuture;

        // Parse DD-MM-YYYY to YYYY-MM-DD for reliable comparison
        const parsed = moment(nfd, "DD-MM-YYYY");
        if (!parsed.isValid()) return isMarkedFuture;

        const nfdFormatted = parsed.format("YYYY-MM-DD");

        // Show if: date is in the future OR status is "4"
        return nfdFormatted > today || isMarkedFuture;
      });

      return {
        data: futureData,
        total: futureData.length,
        totalPages: Math.ceil(futureData.length / 10) || 1,
      };
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: "always",
  });
};


// ── NEW: Done / Converted leads ──────────────────────────────────────────────
// export const useDoneLeads = (page, filters = {}) => {
//   return useQuery({
//     queryKey: ["done-leads", page, filters],
//     queryFn: async () => {
//       const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
//         headers: headers(),
//         params: {
//           page,
//           limit: 10,
//           status: 7,
//           search: filters.search || "",
//           start_date: filters.start_date || "",
//           end_date: filters.end_date || "",
//         },
//       });
//       return {
//         data: res.data?.data || [],
//         total: res.data?.total_records || 0,
//         totalPages: Math.ceil((res.data?.total_records || 0) / 10),
//       };
//     },
//     keepPreviousData: true,
//   });
// };

export const useDoneLeads = (page, filters = {}) => {
  return useQuery({
    queryKey: ["done-leads", page, filters],
    queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
        headers: headers(),
        params: {
          page,
          limit: 10,
          status: 7,
          search: filters.search || "",
          start_date: filters.start_date || "",
          end_date: filters.end_date || "",
        },
      });

      const allData = res.data?.data || [];

      // Client-side filter: keep only truly converted leads
      // since backend is not filtering by status param yet
      const convertedData = allData.filter(
        (item) => String(item.status) === "7",
      );

      return {
        data: convertedData,
        total: convertedData.length,
        totalPages: Math.ceil(convertedData.length / 10) || 1,
      };
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

// ── NEW: Mark lead as "future" ───────────────────────────────────────────────
export const useMarkFuture = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, remarks }) => {
      const res = await axios.post(
        `${API_BASE_URL}/update-lead-stage`,
        { id, stage: "future", remarks: remarks || "" },
        { headers: headers() },
      );
      if (res.data && res.data.status === false)
        throw new Error(res.data.message);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Lead marked for future follow-up");
      qc.invalidateQueries({ queryKey: ["enquiry-list"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
      qc.invalidateQueries({ queryKey: ["future-leads"] });
    },
    onError: (err) => toast.error(err?.message || "Failed to mark as future"),
  });
};

// ── NEW: Mark lead as "converted" / done ─────────────────────────────────────
export const useMarkDone = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, remarks }) => {
      const res = await axios.post(
        `${API_BASE_URL}/update-lead-stage`,
        { id, stage: "converted", remarks: remarks || "" },
        { headers: headers() },
      );
      if (res.data && res.data.status === false)
        throw new Error(res.data.message);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Lead marked as converted!");
      qc.invalidateQueries({ queryKey: ["enquiry-list"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
      qc.invalidateQueries({ queryKey: ["future-leads"] });
      qc.invalidateQueries({ queryKey: ["done-leads"] });
    },
    onError: (err) => toast.error(err?.message || "Failed to mark as done"),
  });
};

// ==================== MUTATION HOOKS ====================

/**
 * Hook to delete one or multiple enquiries
 */
export const useDeleteEnquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: enquiryService.deleteEnquiry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enquiryList"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete enquiry");
    },
  });
};

/**
 * Hook to update enquiry status
 */
export const useUpdateEnquiryStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: enquiryService.updateEnquiryStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["enquiryList"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["inbox-leads"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["future-leads"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["done-leads"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["negative-leads"],
        refetchType: "all",
      });
      toast.success("Lead status updated successfully!");
    },
    onError: (error) => {
      return
      // toast.error(error.message || "Failed to update enquiry status");
    },
  });
};

/**
 * Hook to import enquiries from Excel file
 */
export const useImportEnquiries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: enquiryService.importEnquiries,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["enquiryList"] });
      toast.success(data?.message || "Enquiries imported successfully!");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.errors?.excel?.[0] ||
        error?.message ||
        "Failed to import enquiries";
      toast.error(message);
    },
  });
};
